import React, { useEffect, useMemo, useState } from 'react'
import { TextField, Stack, Box, InputAdornment } from '@mui/material'
import { getBindMailCode, bindMail, getSecurityOptions } from '@/api/user'
import useCountDown from '@/hooks/useCountDown'
import Modal2 from '@/components/Modal2'
import { Controller, useForm } from 'react-hook-form'
import { Message } from '@cx/ui'
import { useRequest } from 'ahooks'
import SecurityVerifyPanel from './SecurityVerifyPanel'

interface BindEmailModalProps {
  open: boolean
  onCancel: () => void
  onOk: () => void
  currentMail?: string
  currentPhone?: string
}

const BindEmailModal: React.FC<BindEmailModalProps> = ({ open, onCancel, onOk, currentMail, currentPhone }) => {
  const [verifyType, setVerifyType] = useState('')
  const {
    control,
    formState: { errors },
    handleSubmit,
    getValues,
    trigger,
    reset,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      mail: '',
      code: '',
      verify_code: '',
      verify_password: '',
      verify_state: '',
    },
  })
  const [countDown, start] = useCountDown('BIND_EMAIL_COUNT_DOWN')
  const hasBoundMail = useMemo(() => Boolean(currentMail && currentMail.trim()), [currentMail])
  const emailCodeScene = hasBoundMail ? 'change_email' : undefined

  const { data: securityOptions, runAsync: loadSecurityOptions } = useRequest(() => getSecurityOptions('change_email'), {
    manual: true,
  })

  useEffect(() => {
    if (open && hasBoundMail) {
      loadSecurityOptions().then((res) => {
        setVerifyType(res?.recommended_verify_type || res?.available_verify_types?.[0] || '')
      })
    }
    if (!open) {
      reset()
      setVerifyType('')
    }
  }, [open, hasBoundMail, loadSecurityOptions, reset])

  const getEmailCode = async () => {
    if (countDown > 0) return
    trigger('mail').then((res) => {
      if (res) {
        getBindMailCode({ mail: getValues('mail').trim(), scene: emailCodeScene }).then(() => {
          start()
        })
      }
    })
  }

  const onSubmit = handleSubmit((data) => {
    bindMail({
      mail: data.mail.trim(),
      code: data.code,
      verify_type: hasBoundMail ? (verifyType as 'phone' | 'email' | 'wechat') : undefined,
      verify_code: data.verify_code,
      verify_password: data.verify_password,
      verify_state: data.verify_state,
    }).then(() => {
      onOk()
      onCancel()
      Message.success(hasBoundMail ? '修改成功' : '绑定成功')
    })
  })

  return (
    <Modal2
      title={hasBoundMail ? '修改邮箱' : '绑定邮箱'}
      width={625}
      open={open}
      onCancel={onCancel}
      onOk={onSubmit}
    >
      <Stack gap={3}>
        {hasBoundMail && (
          <SecurityVerifyPanel
            scene='change_email'
            open={open}
            options={securityOptions}
            verifyType={verifyType}
            onVerifyTypeChange={setVerifyType}
            control={control}
            errors={errors}
            trigger={trigger}
            setValue={setValue}
            watch={watch}
            currentPhone={currentPhone}
            currentMail={currentMail}
          />
        )}
        <Controller
          rules={{
            required: '请输入邮箱',
          }}
          name='mail'
          control={control}
          render={({ field }) => (
            <TextField
              required
              {...field}
              variant='outlined'
              placeholder='请输入新邮箱'
              label={hasBoundMail ? '新邮箱' : '邮箱'}
              fullWidth
              error={Boolean(errors.mail)}
              helperText={errors.mail?.message as string}
              size='small'
              autoComplete='off'
            />
          )}
        />
        <Controller
          rules={{ required: '请输入验证码' }}
          name='code'
          control={control}
          render={({ field }) => (
            <TextField
              required
              {...field}
              variant='outlined'
              placeholder='请输入验证码'
              label='新邮箱验证码'
              fullWidth
              error={Boolean(errors.code)}
              helperText={errors.code?.message as string}
              size='small'
              autoComplete='off'
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <Box
                      sx={{
                        color: countDown > 0 ? 'rgba(0,0,0,0.5)' : 'primary.main',
                        fontSize: 14,
                        cursor: 'pointer',
                      }}
                      onClick={getEmailCode}
                    >
                      {countDown && countDown > 0 ? `${countDown}s 后重新获取` : '获取验证码'}
                    </Box>
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
      </Stack>
    </Modal2>
  )
}

export default BindEmailModal
